import {
  getAdminUsersExportCsvUrl,
  resolveAdminUsersExportPath,
} from './getAdminUsersExportCsvUrl';

export type AdminUsersExportCsvErrorKind = 'unauthorized' | 'forbidden' | 'not_found' | 'unknown';

export type FetchAdminUsersExportCsvResult =
  | { ok: true; blob: Blob; suggestedFilename?: string }
  | { ok: false; kind: AdminUsersExportCsvErrorKind; status?: number };

export type FetchAdminUsersExportCsvOptions = {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
};

function resolveBaseUrl(override?: string): string {
  if (override) return override;
  const fromEnv = import.meta.env.VITE_API_URL as string | undefined;
  return fromEnv || '';
}

export function parseFilenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  const starMatch = header.match(/filename\*=(?:UTF-8''|utf-8'')([^;\s]+)/i);
  if (starMatch?.[1]) {
    try {
      return decodeURIComponent(starMatch[1].replace(/["']/g, ''));
    } catch {
      return undefined;
    }
  }
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const plain = header.match(/filename=([^;\s]+)/i);
  if (plain?.[1]) return plain[1].replace(/["']/g, '');
  return undefined;
}

export async function fetchAdminUsersExportCsv(
  idToken: string | null | undefined,
  options?: FetchAdminUsersExportCsvOptions
): Promise<FetchAdminUsersExportCsvResult> {
  if (!idToken) {
    return { ok: false, kind: 'unauthorized', status: 401 };
  }

  const baseUrl = resolveBaseUrl(options?.baseUrl);
  if (!baseUrl) {
    return { ok: false, kind: 'unknown' };
  }

  const path = resolveAdminUsersExportPath();
  const url = getAdminUsersExportCsvUrl(baseUrl, path);
  const fetchFn = options?.fetchImpl ?? fetch;

  let response: Response;
  try {
    response = await fetchFn(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  } catch {
    return { ok: false, kind: 'unknown' };
  }

  if (response.status === 200) {
    const blob = await response.blob();
    const cd = response.headers.get('Content-Disposition');
    const suggestedFilename = parseFilenameFromContentDisposition(cd);
    return { ok: true, blob, suggestedFilename };
  }

  if (response.status === 401) {
    return { ok: false, kind: 'unauthorized', status: 401 };
  }
  if (response.status === 403) {
    return { ok: false, kind: 'forbidden', status: 403 };
  }
  if (response.status === 404) {
    return { ok: false, kind: 'not_found', status: 404 };
  }

  return { ok: false, kind: 'unknown', status: response.status };
}
